// All upload doors reserve their member ids before transferring bytes. Only a
// registering batch accepts new members; closing it preserves registered files
// and records every missing member as an error. The reaper uses the same contract
// if the browser disappears before closing the batch.
import {
  collection, doc, runTransaction, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { deleteObject, listAll, ref, uploadBytesResumable } from 'firebase/storage';
import { db, storage } from './firebase';
import {
  FILES, UNALLOCATED_FILES, companyScope, fileTypeOf, newVersion, tableScope,
} from '../utils/blobPaths';
import { memberState } from '../utils/ingestionStatus';

const UPLOAD_CHUNK = 5;
// Kept in sync with ingestion/upload_registration.py. The reaper uses this
// existing field to find cleanup work without scanning all completed history.
export const REGISTRATION_INCOMPLETE_ERROR = 'Some files did not finish uploading.';

export async function finishUploadRegistration({
  companyId, tableId, uploadId, workspaceOrigin = false,
  originalMemberIds = [], confirmedMemberIds = [],
}) {
  const scope = tableId ? tableScope(companyId, tableId) : companyScope(companyId);
  const uploadRef = doc(db, `${scope}/uploads/${uploadId}`);
  const workspaceRef = tableId && workspaceOrigin
    ? doc(db, `users/${companyId}/uploads/${uploadId}`) : null;

  return runTransaction(db, async (transaction) => {
    const uploadSnap = await transaction.get(uploadRef);
    if (!uploadSnap.exists()) throw new Error('The upload batch no longer exists.');
    const upload = uploadSnap.data();
    const workspaceSnap = workspaceRef ? await transaction.get(workspaceRef) : null;
    const manifest = { ...(upload.raw_manifest || {}) };
    const manifestIds = Object.keys(manifest);
    // Only the members this browser did not see register are read. A confirmed
    // member's create committed, so its document exists and carries this upload;
    // a later delete removes its manifest entry in the same commit, so there is
    // nothing about it left to decide. Reading it anyway made the seal wait on
    // the backend: a transaction is refused if any document it read has changed
    // by the time it commits, and cleaning rewrites a file document many times,
    // so across a large upload some read was always stale until cleaning
    // settled. The members still read are the ones whose registration this
    // browser cannot vouch for: a failed transfer or create, a lost reply, or a
    // member it never created itself (a sheet split's children).
    const confirmed = new Set(confirmedMemberIds);
    const memberIds = [...new Set([...manifestIds, ...originalMemberIds])].filter(id => !confirmed.has(id));
    const stagingRefs = memberIds.map(id => doc(db, `${scope}/unallocated_files/${id}`));
    const fileRefs = tableId ? memberIds.map(id => doc(db, `${scope}/files/${id}`)) : [];
    const staging = await Promise.all(stagingRefs.map(memberRef => transaction.get(memberRef)));
    const files = await Promise.all(fileRefs.map(memberRef => transaction.get(memberRef)));
    const missingIds = memberIds.filter((id, index) => (
      ['pending', 'error'].includes(manifest[id])
      && !staging[index].exists() && !files[index]?.exists()
    ));
    const unregisteredIds = originalMemberIds.filter(id => {
      if (confirmed.has(id)) return false;
      const index = memberIds.indexOf(id);
      const owned = [staging[index], files[index]].some(snapshot => (
        snapshot?.exists() && snapshot.data().upload_id === uploadId
      ));
      // Routing and an atomic sheet split leave durable registration receipts
      // even after the original staging document has been consumed.
      const handedOver = ['allocated', 'ok'].includes(manifest[id])
        || manifestIds.some(childId => childId.startsWith(`${id}-`));
      return !owned && !handedOver;
    });

    if (upload.status === 'registering') {
      missingIds.forEach(id => { manifest[id] = 'error'; });
      const drainedWorkspace = !tableId && !Object.values(manifest).includes('pending');
      transaction.update(uploadRef, {
        raw_manifest: manifest,
        status: drainedWorkspace ? 'completed_with_errors' : 'uploaded',
        sealed_at: serverTimestamp(),
        ...(drainedWorkspace ? { completed_at: serverTimestamp() } : {}),
        ...(missingIds.length > 0 ? { error: REGISTRATION_INCOMPLETE_ERROR } : {}),
      });
    }
    if (workspaceRef && workspaceSnap?.exists()
        && ['registering', 'uploaded'].includes(workspaceSnap.data().status)) {
      const workspaceManifest = { ...(workspaceSnap.data().raw_manifest || {}) };
      Object.entries(manifest).forEach(([id, status]) => {
        if (status === 'error' && Object.hasOwn(workspaceManifest, id)) workspaceManifest[id] = 'error';
      });
      transaction.update(workspaceRef, {
        raw_manifest: workspaceManifest,
        status: Object.values(workspaceManifest).includes('error') ? 'completed_with_errors' : 'completed',
        completed_at: serverTimestamp(),
        ...(missingIds.length > 0 ? { error: REGISTRATION_INCOMPLETE_ERROR } : {}),
      });
    }
    return { missingIds, unregisteredIds };
  });
}

// tableId omitted: workspace triage; tableId: table allocation; profileId:
// direct profile upload; workspaceOrigin: a bucket drop with both boundaries.
// `onRegistered` is told each member's reserved document id, paired with its
// file, once the batch is written and before any bytes move — the moment a
// host can show a placeholder that the member's real document will replace.
export async function registerUploadBatch({
  companyId, tableId = null, profileId = null, workspaceOrigin = false,
  files, attributeMap = {}, splitSheets = false, signal, onProgress, onRegistered,
}) {
  if (files.length === 0 || signal?.aborted) {
    return { uploadId: null, memberIds: [], failed: [], cancelled: Boolean(signal?.aborted) };
  }
  const scope = tableId ? tableScope(companyId, tableId) : companyScope(companyId);
  const metadata = { ...attributeMap };
  const attributes = Object.keys(metadata).length > 0 ? { pack_attributes: metadata } : {};
  // The searchable `key::value` form of a direct upload's attributes. A null, a
  // Yes/No value, a map or a list is written as compact JSON, as the backend's
  // approve_allocation writes the smart door's files, so both doors agree.
  const metadataArray = Object.entries(metadata).map(([key, value]) => {
    const isJsonValue = value === null || typeof value === 'boolean' || typeof value === 'object';
    return `${key}::${isJsonValue ? JSON.stringify(value) : value}`;
  });
  const memberCollection = profileId ? 'files' : 'unallocated_files';
  const family = profileId ? FILES : UNALLOCATED_FILES;
  const uploadRef = doc(collection(db, `${scope}/uploads`));
  const uploadId = uploadRef.id;
  const members = files.map(file => {
    const memberRef = doc(collection(db, `${scope}/${memberCollection}`));
    return { file, memberRef, storagePath: newVersion(scope, family, memberRef.id, file.name) };
  });
  const manifest = Object.fromEntries(members.map(({ memberRef }) => [memberRef.id, 'pending']));
  const boundaries = writeBatch(db);
  boundaries.set(uploadRef, {
    created_at: serverTimestamp(), status: 'registering', raw_manifest: manifest,
    method: profileId ? 'direct' : tableId ? 'smart' : 'workspace_smart',
    ...(tableId ? { split_sheets: Boolean(splitSheets) } : { routing: {}, ...attributes }),
    ...(profileId ? { direct_profile_id: profileId, metadata } : {}),
    ...(workspaceOrigin ? { origin_upload_id: uploadId } : {}),
  });
  if (workspaceOrigin) {
    boundaries.set(doc(db, `users/${companyId}/uploads/${uploadId}`), {
      created_at: serverTimestamp(), method: 'workspace_smart', status: 'registering',
      raw_manifest: Object.fromEntries(members.map(({ memberRef }) => [memberRef.id, 'allocated'])),
      routing: Object.fromEntries(members.map(({ memberRef }) => [memberRef.id, { table_ids: [tableId] }])),
      ...attributes,
    });
  }
  await boundaries.commit();
  onRegistered?.(members.map(({ file, memberRef }) => ({ id: memberRef.id, file })));

  const errors = new Map();
  const startedTransfers = new Set();
  const confirmedMembers = new Set();
  let registrationClosed = false;
  let completed = 0;
  const registerMember = async ({ file, memberRef, storagePath }) => {
    let task;
    const cancel = () => task?.cancel();
    try {
      if (signal?.aborted) throw new Error('Upload cancelled before the file registered.');
      if (registrationClosed) throw new Error('This upload has closed. Add this file again in a new upload.');
      startedTransfers.add(memberRef.id);
      task = uploadBytesResumable(ref(storage, storagePath), file);
      signal?.addEventListener('abort', cancel, { once: true });
      if (signal?.aborted) task.cancel();
      await task;
      // A direct upload creates its file in the shape every reader expects
      // from the first moment (Spec 1): the empty containers included, which the
      // security rules require, so the file cannot skip intake.
      const member = {
        name: file.name, created_at: serverTimestamp(), prefix: storagePath, upload_id: uploadId,
        ...(profileId ? {
          type: fileTypeOf(file.name),
          data_profile_id: profileId, metadata,
          metadataArray,
          pre_converted_prefix: null, pre_split_original_prefix: null,
          status: 'Cleaning', processes: {}, assets: {}, pool: {}, units: {}, verdicts: [],
          has_warnings: false, approved: null, date: null, claim: null,
          attempts: 0, transient_exits: 0, cleanup_attempts: 0, cost_usd: 0, woken_at: null,
          deletion: null, batch_id: null, batch_status: null,
        } : {
          status: 'analysing', split_sheets: Boolean(splitSheets), ...attributes,
        }),
      };
      const registered = await runTransaction(db, async (transaction) => {
        const batchSnap = await transaction.get(uploadRef);
        const existing = await transaction.get(memberRef);
        if (existing.exists()) {
          if (existing.data().upload_id !== uploadId) throw new Error('This file belongs to another upload.');
          return true;
        }
        if (signal?.aborted) throw new Error('Upload cancelled before the file registered.');
        if (!batchSnap.exists() || batchSnap.data().status !== 'registering') {
          return false;
        }
        if (batchSnap.data().raw_manifest?.[memberRef.id] !== 'pending') {
          throw new Error('This upload has closed. Add this file again in a new upload.');
        }
        transaction.set(memberRef, member);
        return true;
      });
      if (!registered) {
        registrationClosed = true;
        throw new Error('This upload has closed. Add this file again in a new upload.');
      }
      confirmedMembers.add(memberRef.id);
    } catch (error) {
      errors.set(memberRef.id, error);
    } finally {
      signal?.removeEventListener('abort', cancel);
      completed += 1;
      onProgress?.(completed, files.length);
    }
  };
  // Every door transfers five files at a time. Cancelling stops the transfers in
  // flight and visits every unstarted file, so each is accounted for when the
  // batch closes and the close removes any bytes that did land.
  for (let start = 0; start < members.length; start += UPLOAD_CHUNK) {
    await Promise.all(members.slice(start, start + UPLOAD_CHUNK).map(registerMember));
  }

  let result;
  try {
    result = await finishUploadRegistration({
      companyId, tableId, uploadId, workspaceOrigin,
      originalMemberIds: members.map(member => member.memberRef.id),
      confirmedMemberIds: [...confirmedMembers],
    });
  } catch (error) {
    // A lost response may follow a committed seal. Never turn that batch back
    // into failed/registering; the reaper will finish any genuinely open batch.
    throw new Error('Files may have registered, but the upload status could not be confirmed. '
      + 'Check the files before uploading them again.', { cause: error });
  }
  const missing = new Set(result.unregisteredIds);
  for (const member of members) {
    if (!missing.has(member.memberRef.id) || !startedTransfers.has(member.memberRef.id)) continue;
    try {
      await deleteObject(ref(storage, member.storagePath));
    } catch (error) {
      if (error.code !== 'storage/object-not-found') {
        console.error('Could not remove an unregistered upload object:', error);
      }
    }
  }
  return {
    uploadId,
    memberIds: members.filter(member => !missing.has(member.memberRef.id)).map(member => member.memberRef.id),
    failed: members.filter(member => missing.has(member.memberRef.id)).map(member => ({
      id: member.memberRef.id,
      file: member.file,
      error: errors.get(member.memberRef.id) || new Error('The file did not register before the upload closed.'),
    })),
    cancelled: Boolean(signal?.aborted),
  };
}

async function readUploadErrors(transaction, { companyId, tableId, uploadId }) {
  const scope = tableId ? tableScope(companyId, tableId) : companyScope(companyId);
  const uploadRef = doc(db, `${scope}/uploads/${uploadId}`);
  const snapshot = await transaction.get(uploadRef);
  if (!snapshot.exists()) return { missing: [], settled: false, targets: [], watchPaths: [] };
  const upload = snapshot.data();
  const manifest = upload.raw_manifest || {};
  const finalStatuses = ['completed', 'completed_with_errors', 'failed'];
  let settled = finalStatuses.includes(upload.status);
  const targets = [{ ref: uploadRef, data: upload, ids: [] }];
  const childTargets = new Map();
  const watchPaths = [];
  if (!tableId) {
    const destinations = new Set(Object.values(upload.routing || {}).flatMap(route => (
      route.table_ids || (route.table_id ? [route.table_id] : [])
    )));
    for (const destination of destinations) {
      const tableRef = doc(db, tableScope(companyId, destination));
      const table = await transaction.get(tableRef);
      if (!table.exists()) continue;
      const childRef = doc(db, `${tableScope(companyId, destination)}/uploads/${uploadId}`);
      const child = await transaction.get(childRef);
      if (!child.exists() || !finalStatuses.includes(child.data().status)) {
        settled = false;
        watchPaths.push(childRef.path);
        if (!child.exists()) watchPaths.push(tableRef.path);
      }
      if (child.exists()) childTargets.set(destination, { ref: childRef, data: child.data(), ids: [] });
    }
  }
  const missing = [];
  for (const [memberId, status] of Object.entries(manifest)) {
    const stagingRef = doc(db, `${scope}/unallocated_files/${memberId}`);
    const fileRef = tableId ? doc(db, `${scope}/files/${memberId}`) : null;
    const staging = await transaction.get(stagingRef);
    const fileSnap = fileRef ? await transaction.get(fileRef) : null;
    if (staging.exists() && staging.data().status !== 'Failed') {
      settled = false;
      if (finalStatuses.includes(upload.status)) watchPaths.push(stagingRef.path);
    }
    if (fileSnap?.exists() && memberState(fileSnap.data()) === 'pending') {
      settled = false;
      if (finalStatuses.includes(upload.status)) watchPaths.push(fileRef.path);
    }
    if (status !== 'error' || staging.exists() || fileSnap?.exists()) continue;
    const route = upload.routing?.[memberId] || {};
    const destinations = tableId ? [] : route.table_ids || (route.table_id ? [route.table_id] : []);
    let registeredElsewhere = false;
    const childMissing = [];
    for (const destination of destinations) {
      const childScope = tableScope(companyId, destination);
      const childStaging = await transaction.get(doc(db, `${childScope}/unallocated_files/${memberId}`));
      const childFile = await transaction.get(doc(db, `${childScope}/files/${memberId}`));
      if (childStaging.exists() || childFile.exists()) registeredElsewhere = true;
      else childMissing.push(destination);
    }
    if (registeredElsewhere) continue;
    missing.push({ id: memberId, scope, table: Boolean(tableId) });
    targets[0].ids.push(memberId);
    for (const destination of childMissing) {
      const target = childTargets.get(destination);
      if (target?.data.raw_manifest?.[memberId] === 'error') {
        target.ids.push(memberId);
        missing.push({ id: memberId, scope: tableScope(companyId, destination), table: true });
      }
    }
  }
  if (tableId && upload.origin_upload_id) {
    const originRef = doc(db, `users/${companyId}/uploads/${upload.origin_upload_id}`);
    const origin = await transaction.get(originRef);
    if (origin.exists() && finalStatuses.includes(origin.data().status)) {
      const originData = origin.data();
      const originTarget = { ref: originRef, data: originData, ids: [] };
      for (const member of [...missing]) {
        const route = originData.routing?.[member.id] || {};
        const destinations = route.table_ids || (route.table_id ? [route.table_id] : []);
        if (originData.raw_manifest?.[member.id] !== 'error'
            || destinations.length !== 1 || destinations[0] !== tableId) continue;
        const staging = await transaction.get(doc(db, `users/${companyId}/unallocated_files/${member.id}`));
        if (staging.exists()) continue;
        originTarget.ids.push(member.id);
        missing.push({ id: member.id, scope: companyScope(companyId), table: false });
      }
      targets.push(originTarget);
    }
  }
  targets.push(...childTargets.values());
  return { missing, settled, targets, watchPaths, createdAt: upload.created_at };
}

export function inspectUploadErrors(options) {
  return runTransaction(db, transaction => readUploadErrors(transaction, options));
}

export async function dismissUploadErrors(options) {
  const before = await inspectUploadErrors(options);
  if (!before.settled) throw new Error('Wait for the registered files in this upload to finish.');
  // Closed registration prevents any late browser from creating these members.
  // Clean their own folders before dropping the manifest's remaining handles.
  for (const member of before.missing) {
    // Only the families the browser itself uploads into. A pre-conversion
    // original is written by the backend after registration, so a member that
    // never registered cannot have one, and storage.rules refuses browser
    // deletes of that family.
    const families = member.table ? [UNALLOCATED_FILES, FILES] : [UNALLOCATED_FILES];
    for (const family of families) {
      const folders = [ref(storage, `${member.scope}/${family}/${member.id}/`)];
      while (folders.length > 0) {
        const listed = await listAll(folders.pop());
        folders.push(...listed.prefixes);
        for (const item of listed.items) {
          try {
            await deleteObject(item);
          } catch (error) {
            if (error.code !== 'storage/object-not-found') throw error;
          }
        }
      }
    }
  }
  const cleaned = new Set(before.missing.map(member => `${member.scope}/${member.id}`));
  await runTransaction(db, async (transaction) => {
    const current = await readUploadErrors(transaction, options);
    if (!current.settled) throw new Error('This upload changed. Wait for its files to finish.');
    for (const target of current.targets) {
      const manifest = { ...target.data.raw_manifest };
      const scope = target.ref.path.split('/uploads/')[0];
      const removable = target.ids.filter(id => cleaned.has(`${scope}/${id}`));
      if (removable.length === 0) continue;
      removable.forEach(id => { delete manifest[id]; });
      const allMissingDismissed = target.ids.every(id => removable.includes(id));
      transaction.update(target.ref, {
        raw_manifest: manifest,
        ...(allMissingDismissed && target.data.error === REGISTRATION_INCOMPLETE_ERROR ? { error: null } : {}),
      });
    }
  });
}
